import { useState, useMemo } from 'react';
import styles from './AssignmentBuilder.module.css';
import { ALL_EXPERIMENTS } from '@axlabs/experiments';
import type { ExperimentConfig } from '@axlabs/simulation-core';

interface RubricCriterion {
  id: string;
  name: string;
  maxPoints: number;
  description: string;
}

interface AssignmentData {
  title: string;
  description: string;
  selectedExperiments: string[];
  parameters: {
    attempts: number;
    showHints: boolean;
    allowReset: boolean;
  };
  rubric: RubricCriterion[];
  deadline: Date | null;
  deadlineType: 'hard' | 'soft' | 'none';
}

// Map experiments to UI-friendly format
interface ExperimentDisplay extends ExperimentConfig {
  duration: string;
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced';
}

// Convert experiment config to display format
function toExperimentDisplay(experiment: ExperimentConfig): ExperimentDisplay {
  const difficulty = experiment.difficulty ?? 5;
  let difficultyLevel: ExperimentDisplay['difficultyLevel'];
  if (difficulty <= 3) difficultyLevel = 'beginner';
  else if (difficulty <= 7) difficultyLevel = 'intermediate';
  else difficultyLevel = 'advanced';

  // Estimate duration based on parameter count and complexity
  const baseDuration = 30; // minutes
  const parameterBonus = experiment.parameters.length * 2;
  const duration = `${baseDuration + parameterBonus} min`;

  return {
    ...experiment,
    duration,
    difficultyLevel,
  };
}

export interface AssignmentBuilderProps {
  experiments?: ExperimentConfig[];
  onSubmit?: (assignment: AssignmentData) => void;
}

export function AssignmentBuilder({
  experiments = ALL_EXPERIMENTS,
  onSubmit,
}: AssignmentBuilderProps) {
  // Convert experiments for display and group by domain
  const availableExperiments = useMemo(() => {
    return [...experiments].map(toExperimentDisplay);
  }, [experiments]);

  const experimentsByDomain = useMemo(() => {
    const grouped: Record<string, ExperimentDisplay[]> = {
      physics: [],
      chemistry: [],
      biology: [],
    };
    availableExperiments.forEach(exp => {
      (grouped[exp.domain] ??= []).push(exp);
    });
    return grouped;
  }, [availableExperiments]);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [assignment, setAssignment] = useState<AssignmentData>({
    title: '',
    description: '',
    selectedExperiments: [],
    parameters: {
      attempts: 3,
      showHints: true,
      allowReset: true,
    },
    rubric: [
      { id: 'c1', name: 'Data Collection', maxPoints: 10, description: 'Accurate measurement and recording' },
      { id: 'c2', name: 'Analysis', maxPoints: 15, description: 'Correct calculations and graphing' },
    ],
    deadline: null,
    deadlineType: 'hard',
  });

  const handleExperimentToggle = (experimentId: string) => {
    setAssignment(prev => ({
      ...prev,
      selectedExperiments: prev.selectedExperiments.includes(experimentId)
        ? prev.selectedExperiments.filter(id => id !== experimentId)
        : [...prev.selectedExperiments, experimentId],
    }));
  };

  const handleRubricAdd = () => {
    const newCriterion: RubricCriterion = {
      id: `c${Date.now()}`,
      name: '',
      maxPoints: 10,
      description: '',
    };
    setAssignment(prev => ({
      ...prev,
      rubric: [...prev.rubric, newCriterion],
    }));
  };

  const handleRubricRemove = (id: string) => {
    setAssignment(prev => ({
      ...prev,
      rubric: prev.rubric.filter(c => c.id !== id),
    }));
  };

  const handleRubricChange = (id: string, field: keyof RubricCriterion, value: string | number) => {
    setAssignment(prev => ({
      ...prev,
      rubric: prev.rubric.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      ),
    }));
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(assignment);
    } else {
      console.log('Assignment created:', assignment);
      alert('Assignment created successfully!');
    }
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return assignment.selectedExperiments.length > 0;
      case 2:
        return assignment.title.trim().length > 0;
      case 3:
        return assignment.rubric.length > 0 &&
          assignment.rubric.every(c => c.name.trim().length > 0 && c.maxPoints > 0);
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const STEPS = [
    { id: 1, label: 'Experiments' },
    { id: 2, label: 'Parameters' },
    { id: 3, label: 'Rubric' },
    { id: 4, label: 'Deadline' },
    { id: 5, label: 'Review' },
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        const domains: Array<{ key: string; label: string; icon: string }> = [
          { key: 'physics', label: 'Physics', icon: '⚛️' },
          { key: 'chemistry', label: 'Chemistry', icon: '🧪' },
          { key: 'biology', label: 'Biology', icon: '🧬' },
        ];

        const experimentsToShow = selectedDomain
          ? experimentsByDomain[selectedDomain] ?? []
          : availableExperiments;

        return (
          <>
            <h2 className={styles.stepTitle}>Select Experiments</h2>
            <p className={styles.stepDescription}>
              Choose one or more experiments for this assignment ({availableExperiments.length} available)
            </p>

            {/* Domain Filter */}
            <div className={styles.domainFilter}>
              <button
                className={styles.domainButton}
                data-selected={selectedDomain === null}
                onClick={() => setSelectedDomain(null)}
              >
                All Experiments ({availableExperiments.length})
              </button>
              {domains.map(domain => (
                <button
                  key={domain.key}
                  className={styles.domainButton}
                  data-selected={selectedDomain === domain.key}
                  onClick={() => setSelectedDomain(domain.key)}
                >
                  {domain.icon} {domain.label} ({experimentsByDomain[domain.key]?.length ?? 0})
                </button>
              ))}
            </div>

            <div className={styles.experimentGrid}>
              {experimentsToShow.map(experiment => (
                <label
                  key={experiment.id}
                  className={styles.experimentCard}
                  data-selected={assignment.selectedExperiments.includes(experiment.id)}
                >
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={assignment.selectedExperiments.includes(experiment.id)}
                    onChange={() => handleExperimentToggle(experiment.id)}
                  />
                  <div className={styles.experimentInfo}>
                    <div className={styles.experimentName}>{experiment.name}</div>
                    <div className={styles.experimentDescription}>{experiment.description}</div>
                    <div className={styles.experimentMeta}>
                      <span className={styles.experimentTag}>{experiment.domain}</span>
                      <span>·</span>
                      <span>{experiment.duration}</span>
                      <span>·</span>
                      <span>{experiment.difficultyLevel}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {experimentsToShow.length === 0 && (
              <div className={styles.noExperiments}>
                <p>No experiments found in this category.</p>
              </div>
            )}
          </>
        );

      case 2:
        return (
          <>
            <h2 className={styles.stepTitle}>Set Parameters</h2>
            <p className={styles.stepDescription}>
              Configure assignment settings and student options
            </p>
            <div className={styles.parameterForm}>
              <div className={styles.formGroup}>
                <label className={styles.formGroupLabel}>Assignment Title</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="e.g., Week 3: Kinematics Lab"
                  value={assignment.title}
                  onChange={e => setAssignment(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formGroupLabel}>Description (Optional)</label>
                <textarea
                  className={`${styles.input} ${styles.textarea}`}
                  placeholder="Add instructions or learning objectives..."
                  value={assignment.description}
                  onChange={e => setAssignment(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formGroupLabel}>Maximum Attempts</label>
                <select
                  className={`${styles.input} ${styles.select}`}
                  value={assignment.parameters.attempts}
                  onChange={e => setAssignment(prev => ({
                    ...prev,
                    parameters: { ...prev.parameters, attempts: Number(e.target.value) }
                  }))}
                >
                  <option value={1}>1 attempt</option>
                  <option value={2}>2 attempts</option>
                  <option value={3}>3 attempts</option>
                  <option value={5}>5 attempts</option>
                  <option value={999}>Unlimited</option>
                </select>
                <p className={styles.formGroupHint}>
                  Number of times students can retry the experiment
                </p>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formGroupLabel}>Student Options</label>
                <label className={styles.dateOption}>
                  <input
                    type="checkbox"
                    className={styles.dateOptionRadio}
                    checked={assignment.parameters.showHints}
                    onChange={e => setAssignment(prev => ({
                      ...prev,
                      parameters: { ...prev.parameters, showHints: e.target.checked }
                    }))}
                  />
                  <div className={styles.dateOptionLabel}>
                    <div className={styles.dateOptionTitle}>Show Hints</div>
                    <div className={styles.dateOptionDesc}>
                      Allow students to access contextual hints during experiments
                    </div>
                  </div>
                </label>
                <label className={styles.dateOption}>
                  <input
                    type="checkbox"
                    className={styles.dateOptionRadio}
                    checked={assignment.parameters.allowReset}
                    onChange={e => setAssignment(prev => ({
                      ...prev,
                      parameters: { ...prev.parameters, allowReset: e.target.checked }
                    }))}
                  />
                  <div className={styles.dateOptionLabel}>
                    <div className={styles.dateOptionTitle}>Allow Reset</div>
                    <div className={styles.dateOptionDesc}>
                      Let students reset and restart the experiment
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </>
        );

      case 3:
        return (
          <>
            <h2 className={styles.stepTitle}>Define Rubric</h2>
            <p className={styles.stepDescription}>
              Create grading criteria for the assignment
            </p>
            <div className={styles.rubricList}>
              {assignment.rubric.map(criterion => (
                <div key={criterion.id} className={styles.rubricItem}>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Criterion name"
                    value={criterion.name}
                    onChange={e => handleRubricChange(criterion.id, 'name', e.target.value)}
                  />
                  <input
                    type="number"
                    className={`${styles.input} ${styles.pointsInput}`}
                    placeholder="Pts"
                    min="0"
                    value={criterion.maxPoints || ''}
                    onChange={e => handleRubricChange(criterion.id, 'maxPoints', Number(e.target.value))}
                  />
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Description"
                    value={criterion.description}
                    onChange={e => handleRubricChange(criterion.id, 'description', e.target.value)}
                  />
                  <button
                    className={styles.removeButton}
                    onClick={() => handleRubricRemove(criterion.id)}
                    aria-label="Remove criterion"
                  >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button className={styles.addButton} onClick={handleRubricAdd}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Criterion
            </button>
          </>
        );

      case 4:
        return (
          <>
            <h2 className={styles.stepTitle}>Set Deadline</h2>
            <p className={styles.stepDescription}>
              Configure due date and late submission policy
            </p>
            <div className={styles.deadlineSection}>
              <div className={styles.formGroup}>
                <label className={styles.formGroupLabel}>Due Date</label>
                <div className={styles.datePicker}>
                  <input
                    type="date"
                    className={`${styles.input} ${styles.dateInput}`}
                    value={assignment.deadline ? assignment.deadline.toISOString().split('T')[0] : ''}
                    onChange={e => setAssignment(prev => ({
                      ...prev,
                      deadline: e.target.value ? new Date(e.target.value) : null
                    }))}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formGroupLabel}>Late Submission Policy</label>
                <div className={styles.dateOptions}>
                  <label
                    className={styles.dateOption}
                    data-selected={assignment.deadlineType === 'hard'}
                  >
                    <input
                      type="radio"
                      name="deadlineType"
                      className={styles.dateOptionRadio}
                      checked={assignment.deadlineType === 'hard'}
                      onChange={() => setAssignment(prev => ({ ...prev, deadlineType: 'hard' }))}
                    />
                    <div className={styles.dateOptionLabel}>
                      <div className={styles.dateOptionTitle}>Hard Deadline</div>
                      <div className={styles.dateOptionDesc}>
                        No submissions accepted after the due date
                      </div>
                    </div>
                  </label>
                  <label
                    className={styles.dateOption}
                    data-selected={assignment.deadlineType === 'soft'}
                  >
                    <input
                      type="radio"
                      name="deadlineType"
                      className={styles.dateOptionRadio}
                      checked={assignment.deadlineType === 'soft'}
                      onChange={() => setAssignment(prev => ({ ...prev, deadlineType: 'soft' }))}
                    />
                    <div className={styles.dateOptionLabel}>
                      <div className={styles.dateOptionTitle}>Soft Deadline</div>
                      <div className={styles.dateOptionDesc}>
                        Late submissions accepted with a grade penalty
                      </div>
                    </div>
                  </label>
                  <label
                    className={styles.dateOption}
                    data-selected={assignment.deadlineType === 'none'}
                  >
                    <input
                      type="radio"
                      name="deadlineType"
                      className={styles.dateOptionRadio}
                      checked={assignment.deadlineType === 'none'}
                      onChange={() => setAssignment(prev => ({ ...prev, deadlineType: 'none' }))}
                    />
                    <div className={styles.dateOptionLabel}>
                      <div className={styles.dateOptionTitle}>No Deadline</div>
                      <div className={styles.dateOptionDesc}>
                        Students can complete at their own pace
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </>
        );

      case 5:
        const selectedExpDetails = availableExperiments.filter(e =>
          assignment.selectedExperiments.includes(e.id)
        );
        const totalPoints = assignment.rubric.reduce((sum, c) => sum + (c.maxPoints || 0), 0);

        return (
          <>
            <h2 className={styles.stepTitle}>Review & Assign</h2>
            <p className={styles.stepDescription}>
              Review your assignment before publishing
            </p>
            <div className={styles.reviewSection}>
              <div className={styles.reviewCard}>
                <div className={styles.reviewCardTitle}>Assignment</div>
                <div className={styles.reviewCardContent}>
                  <strong>{assignment.title || 'Untitled Assignment'}</strong>
                  {assignment.description && (
                    <p style={{ marginTop: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
                      {assignment.description}
                    </p>
                  )}
                </div>
              </div>

              <div className={styles.reviewCard}>
                <div className={styles.reviewCardTitle}>
                  Experiments ({selectedExpDetails.length})
                </div>
                <div className={styles.reviewCardContent}>
                  <div className={styles.reviewList}>
                    {selectedExpDetails.map(exp => (
                      <span key={exp.id} className={styles.reviewChip}>
                        {exp.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.reviewCard}>
                <div className={styles.reviewCardTitle}>
                  Rubric ({totalPoints} points total)
                </div>
                <div className={styles.reviewCardContent}>
                  <table className={styles.rubricReviewTable}>
                    <thead>
                      <tr>
                        <th>Criterion</th>
                        <th>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignment.rubric.map(c => (
                        <tr key={c.id}>
                          <td>{c.name || '(unnamed)'}</td>
                          <td>{c.maxPoints}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.reviewCard}>
                <div className={styles.reviewCardTitle}>Settings</div>
                <div className={styles.reviewCardContent}>
                  <div>Attempts: {assignment.parameters.attempts === 999 ? 'Unlimited' : assignment.parameters.attempts}</div>
                  <div>Hints: {assignment.parameters.showHints ? 'Enabled' : 'Disabled'}</div>
                  <div>Reset: {assignment.parameters.allowReset ? 'Allowed' : 'Not allowed'}</div>
                  <div>
                    Deadline:{' '}
                    {assignment.deadline
                      ? assignment.deadline.toLocaleDateString()
                      : 'Not set'}{' '}
                    ({assignment.deadlineType})
                  </div>
                </div>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Create Assignment</h1>
        <p className={styles.subtitle}>
          Build and assign experiments to your students
          {availableExperiments.length > 0 && ` (${availableExperiments.length} experiments available)`}
        </p>
      </header>

      <div className={styles.wizard}>
        {/* Progress Steps */}
        <div className={styles.steps}>
          {STEPS.map(step => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;

            return (
              <div
                key={step.id}
                className={styles.step}
                data-active={isActive}
                data-completed={isCompleted}
              >
                <div className={styles.stepNumber}>
                  {isCompleted ? '✓' : step.id}
                </div>
                <div className={styles.stepLabel}>{step.label}</div>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className={styles.stepContent}>
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className={styles.navigation}>
          <button
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
            disabled={currentStep === 1}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {currentStep < 5 ? (
            <button
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={() => setCurrentStep(s => Math.min(5, s + 1))}
              disabled={!canGoNext()}
            >
              Next
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={handleSubmit}
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Create Assignment
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
